import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableSkeleton } from '../../../components';
import {
  AggregatedStaffCredits,
  AnalyticsReceivableClient,
  AnalyticsTopClient,
  DecimalString,
  WithRegion,
} from '../../../api/types/analytics';
import { formatDateTime, formatInt, formatMoney, orDash, regionName, regionShort } from './format';
import { Card, Empty, Muted, RegionTag, SectionTitle, Subtle, TableScroll, TwoCol } from './styles';

type Column = { title: string; key: string };

const DataTable: FC<{ columns: Column[]; rows: Array<Record<string, React.ReactNode>>; loading: boolean }> = ({ columns, rows, loading }) => {
  const [t] = useTranslation();
  if (loading) return <TableSkeleton rows={5} columns={columns.length} />;
  if (!rows.length) return <Empty>{t('DD_NO_DATA')}</Empty>;
  return (
    <TableScroll>
      <Table columns={columns} rows={rows} hideCheckbox hideOther />
    </TableScroll>
  );
};

const Region: FC<{ region: string }> = ({ region }) => (
  <RegionTag title={regionName(region)}>{regionShort(region)}</RegionTag>
);

const ClientCell: FC<{ name: string | null; email?: string | null }> = ({ name, email }) => (
  <span>
    {orDash(name)}
    {email ? (
      <>
        <br />
        <Muted>{email}</Muted>
      </>
    ) : null}
  </span>
);

const balanceCell = (amount: DecimalString | undefined, currency: string, usd: DecimalString | null | undefined) =>
  currency && currency !== 'USD' ? (
    <span>
      {formatMoney(amount, currency)}
      <br />
      <Muted>≈ {formatMoney(usd, 'USD')}</Muted>
    </span>
  ) : (
    formatMoney(usd, 'USD')
  );

export const TopClientsTable: FC<{ clients: Array<WithRegion<AnalyticsTopClient>>; loading: boolean }> = ({
  clients,
  loading,
}) => {
  const [t] = useTranslation();
  const columns: Column[] = [
    { title: t('DD_COL_CLIENT'), key: 'client' },
    { title: t('DD_COL_REGION'), key: 'region' },
    { title: t('DD_COL_BILLED'), key: 'billed' },
    { title: t('DD_COL_CASH_IN'), key: 'cash_in' },
    { title: t('DD_COL_BALANCE'), key: 'balance' },
  ];
  const rows = clients.map((c) => ({
    id: `${c.region}-${c.client_id}`,
    client: <ClientCell name={c.name} email={c.email} />,
    region: <Region region={c.region} />,
    billed: formatMoney(c.billed_revenue?.amount_usd, 'USD'),
    cash_in: formatMoney(c.cash_in?.amount_usd, 'USD'),
    balance: balanceCell(c.balance?.amount, c.baseCurrency || 'USD', c.balance?.amount_usd),
  }));
  return (
    <Card>
      <SectionTitle>{t('DD_TOP_CLIENTS')}</SectionTitle>
      <DataTable columns={columns} rows={rows} loading={loading} />
    </Card>
  );
};

export const DebtorsTable: FC<{
  clients: Array<WithRegion<AnalyticsReceivableClient>>;
  totalUsd: DecimalString | null | undefined;
  loading: boolean;
}> = ({ clients, totalUsd, loading }) => {
  const [t] = useTranslation();
  const columns: Column[] = [
    { title: t('DD_COL_CLIENT'), key: 'client' },
    { title: t('DD_COL_REGION'), key: 'region' },
    { title: t('DD_COL_DAYS_OVERDUE'), key: 'days' },
    { title: t('DD_COL_TOTAL_DUE'), key: 'total' },
    { title: t('DD_COL_STATUS'), key: 'status' },
  ];
  const rows = clients.map((c) => ({
    id: `${c.region}-${c.client_id}`,
    client: <ClientCell name={c.name} email={c.email} />,
    region: <Region region={c.region} />,
    days: c.days_overdue === null || c.days_overdue === undefined ? '—' : formatInt(c.days_overdue),
    total: formatMoney(c.total_due?.amount_usd, 'USD'),
    status: c.status || '—',
  }));
  return (
    <Card>
      <SectionTitle>{t('DD_DEBTORS')}</SectionTitle>
      {totalUsd !== undefined && !loading && (
        <Subtle>{t('DD_DEBTORS_TOTAL', { value: formatMoney(totalUsd, 'USD') })}</Subtle>
      )}
      <DataTable columns={columns} rows={rows} loading={loading} />
    </Card>
  );
};

export const StaffCreditsSection: FC<{ data: AggregatedStaffCredits | null; loading: boolean }> = ({
  data,
  loading,
}) => {
  const [t] = useTranslation();
  const staffColumns: Column[] = [
    { title: t('DD_COL_STAFF'), key: 'user' },
    { title: t('DD_COL_REGION'), key: 'region' },
    { title: t('DD_COL_OPERATIONS'), key: 'operations' },
    { title: t('DD_COL_ADDED'), key: 'added' },
    { title: t('DD_COL_REMOVED'), key: 'removed' },
    { title: t('DD_COL_EXTERNAL'), key: 'external' },
  ];
  const opColumns: Column[] = [
    { title: t('DD_COL_DATE'), key: 'date' },
    { title: t('DD_COL_REGION'), key: 'region' },
    { title: t('DD_COL_CLIENT'), key: 'client' },
    { title: t('DD_COL_STAFF'), key: 'user' },
    { title: t('DD_COL_KIND'), key: 'kind' },
    { title: t('DD_COL_AMOUNT'), key: 'amount' },
    { title: t('DD_COL_NOTES'), key: 'notes' },
  ];
  const kindLabel = (kind: string) =>
    kind === 'added' ? t('DD_KIND_ADDED') : kind === 'removed' ? t('DD_KIND_REMOVED') : kind === 'external' ? t('DD_KIND_EXTERNAL') : kind;

  const staffRows = (data?.by_staff || []).map((s, i) => ({
    id: `${s.region}-${s.user_id ?? 'sys'}-${i}`,
    user: s.user || t('DD_SYSTEM_USER'),
    region: <Region region={s.region} />,
    operations: formatInt(s.operations),
    added: formatMoney(s.added?.amount_usd, 'USD'),
    removed: formatMoney(s.removed?.amount_usd, 'USD'),
    external: formatMoney(s.external?.amount_usd, 'USD'),
  }));
  const opRows = (data?.operations || []).map((o) => ({
    id: `${o.region}-${o.journal_id}`,
    date: formatDateTime(o.date),
    region: <Region region={o.region} />,
    client: o.client_name || (o.client_id !== null && o.client_id !== undefined ? `#${o.client_id}` : '—'),
    user: o.user || t('DD_SYSTEM_USER'),
    kind: kindLabel(o.kind),
    // Sign is conveyed by the "kind" column; amount is shown as returned by the API.
    amount: formatMoney(o.amount?.amount_usd, 'USD'),
    notes: o.notes || '—',
  }));

  return (
    <Card>
      <SectionTitle>{t('DD_STAFF_CREDITS')}</SectionTitle>
      <Subtle>{t('DD_STAFF_CREDITS_NOTE')}</Subtle>
      {data && !loading && (
        <Subtle>
          {t('DD_STAFF_CREDITS_TOTALS', {
            added: formatMoney(data.totals.added_usd, 'USD'),
            removed: formatMoney(data.totals.removed_usd, 'USD'),
            net: formatMoney(data.totals.net_usd, 'USD'),
            external: formatMoney(data.totals.external_usd, 'USD'),
          })}
          {data.incomplete && <> {t('DD_USD_INCOMPLETE')}</>}
        </Subtle>
      )}
      <TwoCol style={{ marginTop: 12 }}>
        <div>
          <SectionTitle as="h3" style={{ fontSize: 14 }}>
            {t('DD_BY_STAFF')}
          </SectionTitle>
          <DataTable columns={staffColumns} rows={staffRows} loading={loading} />
        </div>
        <div>
          <SectionTitle as="h3" style={{ fontSize: 14 }}>
            {t('DD_RECENT_OPERATIONS')}
          </SectionTitle>
          <DataTable columns={opColumns} rows={opRows} loading={loading} />
        </div>
      </TwoCol>
    </Card>
  );
};
